import { useEffect, useState, useRef, useCallback } from "react";
import { storefrontApiRequest, STOREFRONT_QUERY, type ShopifyProduct } from "@/lib/shopify";
import { ProductCard } from "@/components/ProductCard";
import { GyroPermissionHint } from "@/components/GyroPermissionHint";
import { Loader2, PackageOpen } from "lucide-react";

const FadeInCard = ({ children, index }: { children: React.ReactNode; index: number }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setVisible(true);
      return;
    }
    setIsMobile(window.matchMedia("(max-width: 640px)").matches);
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -40px 0px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const delay = Math.min(index, 11) * (isMobile ? 60 : 110);
  return (
    <div ref={ref} style={{ perspective: "1200px" }} className="will-change-transform">
      <div
        className="relative transition-all duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
        style={{
          opacity: visible ? 1 : 0,
          transformStyle: "preserve-3d",
          transformOrigin: "center bottom",
          transform: visible
            ? "rotateX(0deg) rotateY(0deg) translateY(0) translateZ(0) scale(1)"
            : `rotateX(${isMobile ? -10 : -35}deg) rotateY(${isMobile ? 2 : 6}deg) translateY(${isMobile ? 28 : 60}px) translateZ(${isMobile ? -40 : -120}px) scale(${isMobile ? 0.97 : 0.92})`,
          transitionDelay: `${delay}ms`,
        }}
      >
        {visible && (
          <div
            aria-hidden
            className="pointer-events-none absolute -inset-3 rounded-[inherit] opacity-0 animate-card-halo"
            style={{
              background: "radial-gradient(closest-side, hsl(var(--gold) / 0.55), hsl(var(--gold) / 0.15) 55%, transparent 75%)",
              filter: "blur(18px)",
              animationDelay: `${delay}ms`,
              zIndex: -1,
            }}
          />
        )}
        {children}
      </div>
    </div>
  );
};

export const ProductGrid = () => {
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchProducts() {
      try {
        const data = await storefrontApiRequest(STOREFRONT_QUERY, { first: 20 });
        if (data?.data?.products?.edges) {
          // Filter out the donation product and the tip ("pourboire") product from the storefront grid
          const filtered = data.data.products.edges.filter((p: ShopifyProduct) => {
            const handle = p.node.handle?.toLowerCase() ?? "";
            const title = p.node.title?.toLowerCase() ?? "";
            const productType = ((p.node as { productType?: string }).productType ?? "").toLowerCase();
            const tags = (p.node.tags ?? []).map((t) => t.toLowerCase());
            if (handle === "don-solidaire") return false;
            if (productType === "tip" || productType === "pourboire" || productType === "donation") return false;
            if (tags.includes("tip") || tags.includes("pourboire")) return false;
            if (handle.includes("pourboire") || handle.includes("tip")) return false;
            if (title.includes("pourboire")) return false;
            return true;
          });
          setProducts(filtered);
        }
      } catch (error) {
        console.error("Failed to fetch products:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchProducts();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="text-center py-20">
        <PackageOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-xl font-display font-semibold mb-2">Aucun produit pour l'instant</h3>
        <p className="text-muted-foreground font-body">
          Les adhésifs arrivent bientôt ! Restez connectés 🇹🇳
        </p>
      </div>
    );
  }

  return (
    <>
      <GyroPermissionHint />
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
        {products.map((product, i) => (
          <FadeInCard key={product.node.id} index={i}>
            <ProductCard product={product} />
          </FadeInCard>
        ))}
      </div>
    </>
  );
};
